import 'Components/vm-icon/vm-icon';
import { html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import Styles from './vm-suggested-questions.scss';

const DEFAULT_SKELETON_COUNT = 5;
const SKELETON_WIDTHS = ['78%', '64%', '88%', '54%', '72%'];

/**
 * A list of clickable suggested questions.
 *
 * Fires `vm-select` with `detail.question` when a question is clicked.
 *
 * @fires vm-select - CustomEvent<{ question: string }>
 */
@customElement('vm-suggested-questions')
export class VmSuggestedQuestions extends LitElement {
    static override styles = Styles;

    /** Questions to render. */
    @property({ type: Array })
    questions: string[] = [];

    /** Visual variant. */
    @property({ reflect: true })
    variant: 'card' | 'minimal' = 'card';

    /** Render skeleton placeholders instead of questions. Only honored for the minimal variant. */
    @property({ type: Boolean })
    loading = false;

    /** Number of skeleton rows to render while loading. */
    @property({ type: Number, attribute: 'skeleton-count' })
    skeletonCount = DEFAULT_SKELETON_COUNT;

    private _select(question: string) {
        this.dispatchEvent(
            new CustomEvent('vm-select', {
                detail: { question },
                bubbles: true,
                composed: true,
            }),
        );
    }

    private _renderSkeleton() {
        return html`
            <div class="list" aria-busy="true" aria-label="Loading suggested questions">
                ${Array.from({ length: this.skeletonCount }).map(
                    (_, i) => html`
                        <div class="skeleton">
                            <div class="skeleton__icon"></div>
                            <div
                                class="skeleton__text"
                                style="width: ${SKELETON_WIDTHS[i % SKELETON_WIDTHS.length]}"
                            ></div>
                        </div>
                    `,
                )}
            </div>
        `;
    }

    override render() {
        if (this.loading && this.variant === 'minimal') {
            return this._renderSkeleton();
        }

        if (this.questions.length === 0) return nothing;

        return html`
            <div class="list">
                ${repeat(
                    this.questions,
                    (q) => q,
                    (q) => html`
                        <button type="button" class="btn" @click=${() => this._select(q)}>
                            ${this.variant === 'minimal'
                                ? html`<vm-icon class="icon" name="search" size="sm"></vm-icon>`
                                : nothing}
                            <span>${q}</span>
                        </button>
                    `,
                )}
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-suggested-questions': VmSuggestedQuestions;
    }
}
